import { createAuthorisedApiRequest } from "../helpers/create-authorised-request";
import { fetchWrapper } from "../helpers/fetch-wrapper";
import { AuthenticationStatus, SmokeballAuthenticator } from "../services/smokeball-authenticator";
import { useLoggerStore } from "./logger_store";
import { Subject, Subscription, timer } from "rxjs";
import { concatMap, map } from "rxjs/operators";
import { localConsoleLog } from "./local_console_log";

const validLogLevels = ["debug", "info", "warn", "error"] as const;
type TLogLevel = (typeof validLogLevels)[number];
type TLogBufferRecord = { logMessage: TLogMessage; retryCount: number };

type TLogMessage = {
  message: string;
  level: TLogLevel;
  sessionId: string;
  interactionId: string;
  app: "OUTLOOK_WEB_ADDIN";
  timestamp: string;
};

const logSubject = new Subject<TLogBufferRecord>();

const attemptToReaddLogMessagesToBuffer = (logs: TLogBufferRecord[]) => {
  logs.forEach((log) => {
    // If this particular message has been retried more than 3 times, discard it
    if (log.retryCount++ > 3) {
      return;
    }
    // Otherwise return it to the pool
    logSubject.next(log);
  });
};
logSubject
  .pipe(
    concatMap((log) => timer(1000).pipe(map(() => log))) // Emits each log, spaced 1 seconds apart
  )
  .subscribe(async (logBufferRecord) => {
    try {
      const authenticator = SmokeballAuthenticator.getSmokeballAuthenticator();
      const authState = await authenticator.getAuthStatusWithoutAttemptingRefresh();
      if (authState == AuthenticationStatus.LoggedOut) {
        // If signed out, add the logs back onto the buffer for retrying after a 30 second delay
        setTimeout(() => {
          attemptToReaddLogMessagesToBuffer([logBufferRecord]);
        }, 30000);
        return;
      }

      const body: {
        log: TLogMessage;
      } = { log: logBufferRecord.logMessage };
      const { url, requestInit } = await createAuthorisedApiRequest("ClientLog", body);
      const { err } = await fetchWrapper(url, requestInit);

      if (err) {
        attemptToReaddLogMessagesToBuffer([logBufferRecord]);
        localConsoleLog.warn("Batch log sending failed", err);
      }
    } catch (err) {
      localConsoleLog.error("Caught error while sending batch logs", err);
    }
  });

const makeRemoteLogger =
  (level: TLogLevel) =>
  (...bits: unknown[]) => {
    addLogToBuffer(level, ...bits);
  };

const remoteLogger = {
  log: makeRemoteLogger("info"),
  debug: makeRemoteLogger("debug"),
  info: makeRemoteLogger("info"),
  warn: makeRemoteLogger("warn"),
  error: makeRemoteLogger("error"),
};

let replaced = false;

const cleanStringForRemoteLoggingEndpoint = (str: string) => {
  return str?.replace(/https:\/\/localhost:3000/g, "{localMachine}");
};

const addLogToBuffer = (level: TLogLevel, ...bits: unknown[]) => {
  restoreConsoleLogging();

  let logLevel = level;
  if (!validLogLevels.includes(logLevel)) {
    logLevel = "info";
  }

  const { sessionId, interactionId } = useLoggerStore.getState();
  localConsoleLog[logLevel](sessionId, interactionId, ...bits);

  const message = bits.map((b) => cleanStringForRemoteLoggingEndpoint(`${b}`)).join(" ");
  logSubject.next({
    logMessage: {
      message,
      level: logLevel,
      sessionId,
      interactionId,
      app: "OUTLOOK_WEB_ADDIN",
      timestamp: new Date().toISOString(),
    },
    retryCount: 0,
  });

  replaceConsoleLogging();
};
let logSubscription: Subscription;
export const replaceConsoleLogging = () => {
  if (replaced) return;

  replaced = true;
  console.log = remoteLogger.info;
  console.debug = remoteLogger.debug;
  console.info = remoteLogger.info;
  console.warn = remoteLogger.warn;
  console.error = remoteLogger.error;
  logSubscription = logSubject.subscribe(() => {}); // Empty subscription we handle it all in the .pipe().
};

export const restoreConsoleLogging = () => {
  if (!replaced) return;

  replaced = false;
  console.log = localConsoleLog.log;
  console.debug = localConsoleLog.debug;
  console.info = localConsoleLog.info;
  console.warn = localConsoleLog.warn;
  console.error = localConsoleLog.error;
  logSubscription?.unsubscribe();
};
